import type { AwgSize, ConductorMaterial } from "../types.ts";

/**
 * NEC 2023 Chapter 9, Table 8 — Conductor Properties.
 *
 * `cmil`  = area in circular mils.
 * `dcOhmPerKft` = DC resistance at 75°C, ohms per 1000 ft, uncoated
 *               (copper) / aluminum. Used by the voltage-drop calculator.
 *
 * Values transcribed from NEC 2023 Ch.9 Table 8. DO NOT edit a number here
 * without updating the golden test that pins it.
 */
export const TABLE_8: Record<
  AwgSize,
  { cmil: number; dcOhmPerKft: { copper: number; aluminum: number } }
> = {
  "14": { cmil: 4110, dcOhmPerKft: { copper: 3.07, aluminum: 5.06 } },
  "12": { cmil: 6530, dcOhmPerKft: { copper: 1.93, aluminum: 3.18 } },
  "10": { cmil: 10380, dcOhmPerKft: { copper: 1.21, aluminum: 2.0 } },
  "8": { cmil: 16510, dcOhmPerKft: { copper: 0.764, aluminum: 1.26 } },
  "6": { cmil: 26240, dcOhmPerKft: { copper: 0.491, aluminum: 0.808 } },
  "4": { cmil: 41740, dcOhmPerKft: { copper: 0.308, aluminum: 0.508 } },
  "3": { cmil: 52620, dcOhmPerKft: { copper: 0.245, aluminum: 0.403 } },
  "2": { cmil: 66360, dcOhmPerKft: { copper: 0.194, aluminum: 0.319 } },
  "1": { cmil: 83690, dcOhmPerKft: { copper: 0.154, aluminum: 0.253 } },
  "1/0": { cmil: 105600, dcOhmPerKft: { copper: 0.122, aluminum: 0.201 } },
  "2/0": { cmil: 133100, dcOhmPerKft: { copper: 0.0967, aluminum: 0.159 } },
  "3/0": { cmil: 167800, dcOhmPerKft: { copper: 0.0766, aluminum: 0.126 } },
  "4/0": { cmil: 211600, dcOhmPerKft: { copper: 0.0608, aluminum: 0.1 } },
  "250": { cmil: 250000, dcOhmPerKft: { copper: 0.0515, aluminum: 0.0847 } },
  "300": { cmil: 300000, dcOhmPerKft: { copper: 0.0429, aluminum: 0.0707 } },
  "350": { cmil: 350000, dcOhmPerKft: { copper: 0.0367, aluminum: 0.0605 } },
  "400": { cmil: 400000, dcOhmPerKft: { copper: 0.0321, aluminum: 0.0529 } },
  "500": { cmil: 500000, dcOhmPerKft: { copper: 0.0258, aluminum: 0.0424 } },
};

export function dcResistanceOhmPerKft(awg: AwgSize, material: ConductorMaterial): number {
  return TABLE_8[awg].dcOhmPerKft[material];
}

/**
 * NEC 2023 Chapter 9, Table 5 — Dimensions of Insulated Conductors.
 * Approximate area in square inches, by insulation type.
 *
 * v1 supports THHN/THWN/THWN-2 (by far the most common building wire). Adding
 * an insulation type means adding a column here + a golden test for it; the
 * calculator already keys off this table so no calculator change is needed.
 */
export type InsulationType = "THHN" | "XHHW";

export const TABLE_5_AREA_IN2: Record<InsulationType, Record<AwgSize, number>> = {
  // THHN / THWN / THWN-2 (NEC 2023 Ch.9 Table 5)
  THHN: {
    "14": 0.0097,
    "12": 0.0133,
    "10": 0.0211,
    "8": 0.0366,
    "6": 0.0507,
    "4": 0.0824,
    "3": 0.0973,
    "2": 0.1158,
    "1": 0.1562,
    "1/0": 0.1855,
    "2/0": 0.2223,
    "3/0": 0.2679,
    "4/0": 0.3237,
    "250": 0.397,
    "300": 0.4608,
    "350": 0.5242,
    "400": 0.5863,
    "500": 0.7073,
  },
  // XHHW (NEC 2023 Ch.9 Table 5)
  XHHW: {
    "14": 0.0139,
    "12": 0.0181,
    "10": 0.0243,
    "8": 0.0437,
    "6": 0.059,
    "4": 0.0814,
    "3": 0.0962,
    "2": 0.1146,
    "1": 0.1534,
    "1/0": 0.1825,
    "2/0": 0.219,
    "3/0": 0.2642,
    "4/0": 0.3197,
    "250": 0.3904,
    "300": 0.4536,
    "350": 0.5166,
    "400": 0.5782,
    "500": 0.6984,
  },
};

export function conductorAreaIn2(awg: AwgSize, insulation: InsulationType): number {
  return TABLE_5_AREA_IN2[insulation][awg];
}
